import { RightOutlined } from '@ant-design/icons';
import { Collapse, List, Typography, theme } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import { EVENTS } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import analyticsService from 'src/helpers/analytics.service';

/**
 * @param {{
 * textPrefix?: string,
 * timeslot: import('shared/api/rotations.service.api').Rotation,
 * style?: React.CSSProperties,
 * }} props
 */
function Timeslot(props) {
  const { token } = theme.useToken();

  const styles = {
    box: {
      width: '100%',
      padding: token.padding,
      paddingTop: token.paddingSM,
      paddingBottom: token.paddingSM,
      backgroundColor: token.colorBgBase,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
  };

  const intlDateTimeFormat = new Intl.DateTimeFormat(navigator.language, {
    month: 'numeric',
    day: 'numeric',
  });

  const content = (
    <>
      <Typography style={{ color: token.colorTextHeading }}>
        {props.textPrefix ? <strong>{props.textPrefix}: </strong> : null}
        {props.timeslot.rotation.name}, {intlDateTimeFormat.format(props.timeslot.start)} -{' '}
        {intlDateTimeFormat.format(props.timeslot.end)}
      </Typography>
    </>
  );

  if(props.timeslot.rotation.url) {
    return (
      <a
        onClick={() => {
          analyticsService.track(EVENTS.ROTATIONS.OPEN_LINK, {
            rotationName: props.timeslot.rotation.name,
            rotationCode: props.timeslot.rotation.code,
            rotationOrganizationId: props.timeslot.rotation.organizationId,
            rotationOwnerId: props.timeslot.rotation.ownerId,
            rotationUrl: props.timeslot.rotation.url,
          });
        }}
        href={props.timeslot.rotation.url}
        style={{
          ...styles.box,
          width: '100%',
          color: 'inherit',
          ...props.style,
        }}
      >
        {content}
        <RightOutlined />
      </a>
    );
  }

  return (
    <div
      style={{
        ...styles.box,
        ...props.style,
      }}
    >
      {content}
    </div>
  );
}

/**
 * @param {{
 * rotationTimeslots: import('shared/api/rotations.service.api').RotationTimeslot[]
 * }} props
 */
export function RotationsWidgetCollapsible(props) {
  const { token } = theme.useToken();
  const lang = useSelector(getLang('DASHBOARD'));

  const now = new Date().getTime();

  let current;
  let next;
  const rest = [];

  const timeslots = props.rotationTimeslots.map(t => {
    return {
      ...t,
      start: new Date(t.start),
      end: new Date(t.end),
    };
  });

  for (const timeslot of timeslots) {
    if (timeslot.start.getTime() <= now && now < timeslot.end.getTime()) {
      current = timeslot;
    } else if (
      // is timeslot in the future
      timeslot.start.getTime() > now &&
      // is next not set
      (!next ||
        // if timeslot starts earlier than next, it means it's closer to now
        timeslot.start.getTime() < next.start.getTime())
    ) {
      next = timeslot;
    } else if (
      // is timeslot in the future
      timeslot.start.getTime() > now
    ) {
      rest.push(timeslot);
    }
  }

  return (
    <>
      {current ? (
        <div>
          <Timeslot
            style={{
              marginBottom: token.marginSM,
              borderRadius: token.borderRadius,
            }}
            textPrefix={lang.ROTATIONS_CURRENT}
            timeslot={current}
          />
        </div>
      ) : null}
      {next ? (
        <div>
          <Timeslot
            style={{
              marginBottom: token.marginSM,
              borderRadius: token.borderRadius,
            }}
            textPrefix={lang.ROTATIONS_UPCOMING}
            timeslot={next}
          />
        </div>
      ) : null}
      <Collapse accordion style={{ border: 'none' }}>
        <Collapse.Panel header={lang.ROTATIONS_ALL_ROTATIONS} key="1">
          <List
            style={{
              marginLeft: -token.padding,
              marginRight: -token.padding,
              marginTop: -token.padding,
              marginBottom: -token.padding,
            }}
            itemLayout="horizontal"
            dataSource={
              // Do not display more than 12 rotations
              rest.slice(0, 12)
            }
            renderItem={(item, index) => {
              return (
                <List.Item style={{ padding: 0 }}>
                  <Timeslot timeslot={item} />
                </List.Item>
              );
            }}
          />
        </Collapse.Panel>
      </Collapse>
    </>
  );
}
